import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  Menu,
  Radio,
  RadioGroup,
  Slider,
  Typography,
} from "@mui/material"
import React, { useRef, useState } from "react"
import "chart.js/auto"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import { TimeSeriesChart } from "./TimeSeriesChart"
import { RunDto, RunResultsHistoric, RunResultsHistoricAggregate } from "@custom-types/modelRun"
import Button from "@mui/material/Button"
import DownloadChartImageDialog from "@features/results/DownloadChartImageDialog"
import { useLocalStorage } from "usehooks-ts"
import { GroupNodeLabel } from "@features/graph/graphViewModel"
import ShowChartAsTableButton from "@features/results/Result/ShowChartAsTableButton"
import UpdateDisabledIcon from "@mui/icons-material/UpdateDisabled"
import UpdateIcon from "@mui/icons-material/Update"

type TimeSeriesContainerProps = {
  aggregateData: RunResultsHistoricAggregate | undefined
  historicNodes: RunResultsHistoric | undefined
  isFetchingGetRunFilterNode: boolean
  isMultipleNodesMode: boolean
  lagShift: number
  nodes: GroupNodeLabel[] | null
  runs: RunDto[]
  localStoragePrefix: string
}

export function TimeSeriesContainer({
  isFetchingGetRunFilterNode,
  isMultipleNodesMode = false,
  aggregateData,
  historicNodes,
  lagShift = 0,
  nodes,
  runs,
  localStoragePrefix,
}: TimeSeriesContainerProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [showAsTable, setShowAsTable] = useState(false)
  const [options, setOptions] = useLocalStorage<{
    percentileUpperValue: number | string
    sliderUpperValue: number
    percentileLowerValue: number | string
    sliderLowerValue: number
    isMean: boolean
    showMeanLine: boolean
  }>(`${localStoragePrefix}_timeSeriesChart_v2`, {
    percentileUpperValue: 95,
    sliderUpperValue: 70,
    percentileLowerValue: 5,
    sliderLowerValue: 30,
    isMean: false,
    showMeanLine: true,
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- ANY :(
  const chartRef = useRef<any>(null)
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const [showHistoric, setShowHistoric] = useState(false)

  const runIds = runs.map((run) => run.key)
  const percentiles = [
    options.percentileLowerValue === "custom"
      ? +options.sliderLowerValue
      : +options.percentileLowerValue,
    options.percentileUpperValue === "custom"
      ? +options.sliderUpperValue
      : +options.percentileUpperValue,
  ]
    .filter((value) => value > 0)
    .filter((value, index, array) => array.indexOf(value) === index)
    .sort((left, right) => left - right)

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const onPercentileUpperChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        percentileUpperValue: (event.target as HTMLInputElement).value,
      }
    })
  }

  const onPercentileLowerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        percentileLowerValue: (event.target as HTMLInputElement).value,
      }
    })
  }

  const onSliderUpperChange = (event: Event, value: number | number[], activeThumb: number) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        sliderUpperValue: +value,
      }
    })
  }

  const onSliderLowerChange = (event: Event, value: number | number[], activeThumb: number) => {
    setOptions((prevState) => {
      return {
        ...prevState,
        sliderLowerValue: +value,
      }
    })
  }

  const handleResetZoom = () => {
    if (chartRef && chartRef.current) {
      chartRef.current.resetZoom()
    }
  }

  const handleHistoricDataClick = () => {
    setShowHistoric(!showHistoric)
  }

  return (
    <Grid container={true} spacing={1}>
      <Grid item={true} xs={12}>
        <Box>
          <Box
            onClick={handleClick}
            sx={{ height: "48px", lineHeight: "48px", display: "inline-block" }}
          >
            {anchorEl ? (
              <KeyboardArrowUpIcon
                sx={{
                  verticalAlign: "middle",
                  position: "relative",
                  bottom: "2px",
                }}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={{
                  verticalAlign: "middle",
                  position: "relative",
                  bottom: "2px",
                }}
              />
            )}
            {"Chart options"}
          </Box>
          <Button onClick={handleResetZoom} variant={"contained"} sx={{ ml: 2 }}>
            Reset zoom
          </Button>
          <Menu
            id="menu"
            anchorEl={anchorEl}
            open={!!anchorEl}
            onClose={handleClose}
            MenuListProps={{
              "aria-labelledby": "menu",
              sx: { width: "250px" },
            }}
          >
            <FormControlLabel
              sx={{ ml: 1 }}
              control={
                <Checkbox
                  checked={options.showMeanLine}
                  disabled={options.isMean}
                  onChange={(event) =>
                    setOptions({ ...options, showMeanLine: event.target.checked })
                  }
                />
              }
              label="Show mean line"
            />

            <FormControlLabel
              sx={{ ml: 1 }}
              control={
                <Checkbox
                  checked={options.isMean}
                  onChange={({ target: { checked } }) =>
                    setOptions({
                      ...options,
                      isMean: checked,
                      showMeanLine: checked ? true : options.showMeanLine, // auto enable mean line when mean aggregate is enabled
                    })
                  }
                />
              }
              label="Show mean aggregate"
            />
            <FormControl>
              <FormLabel sx={{ ml: 2 }} id="radio-buttons-group-label-lower">
                Lower
              </FormLabel>
              <RadioGroup
                aria-labelledby="radio-buttons-group-label-lower"
                defaultValue="female"
                name="radio-buttons-group"
                value={options.percentileLowerValue}
                onChange={onPercentileLowerChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel
                  value="0"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="None"
                />
                <FormControlLabel
                  value="5"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="5th Percentile"
                />
                <FormControlLabel
                  value="10"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="10th Percentile"
                />
                <FormControlLabel
                  value="25"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="25th Percentile"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="Other"
                />
              </RadioGroup>
              <Slider
                sx={{ ml: 2, width: 220 }}
                min={1}
                max={49}
                defaultValue={30}
                value={options.sliderLowerValue}
                onChange={onSliderLowerChange}
                disabled={options.percentileLowerValue !== "custom" || options.isMean}
                valueLabelDisplay="auto"
              />
            </FormControl>
            <FormControl>
              <FormLabel sx={{ ml: 2 }} id="radio-buttons-group-label-upper">
                Upper
              </FormLabel>
              <RadioGroup
                aria-labelledby="radio-buttons-group-label-upper"
                defaultValue="female"
                name="radio-buttons-group"
                value={options.percentileUpperValue}
                onChange={onPercentileUpperChange}
                sx={{ ml: 2 }}
              >
                <FormControlLabel
                  value="0"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="None"
                />
                <FormControlLabel
                  value="75"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="75th Percentile"
                />
                <FormControlLabel
                  value="90"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="90th Percentile"
                />
                <FormControlLabel
                  value="95"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="95th Percentile"
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size={"small"} disabled={options.isMean} />}
                  label="Other"
                />
              </RadioGroup>
              <Slider
                sx={{ ml: 2, width: 220 }}
                min={51}
                max={99}
                defaultValue={70}
                value={options.sliderUpperValue}
                onChange={onSliderUpperChange}
                disabled={options.percentileUpperValue !== "custom" || options.isMean}
                valueLabelDisplay="auto"
              />
            </FormControl>
          </Menu>

          <IconButton onClick={handleHistoricDataClick} size={"small"}>
            <Typography>Show historic data</Typography>
            {historicNodes && (showHistoric ? <UpdateDisabledIcon /> : <UpdateIcon />)}
          </IconButton>

          {!showAsTable && (
            <DownloadChartImageDialog
              buttonProps={{
                disabled: isFetchingGetRunFilterNode,
                sx: { ml: 1 },
              }}
              chartRef={chartContainerRef}
              defaultFileName="Time-series Chart"
              runReference={runIds.join(" vs. ")}
            />
          )}

          <ShowChartAsTableButton
            showAsTable={showAsTable}
            onClick={() => setShowAsTable((value) => !value)}
          />
        </Box>
      </Grid>
      <Grid item={true} xs={12}>
        {isFetchingGetRunFilterNode ? (
          <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box ref={chartContainerRef}>
            <Typography variant="h6" textAlign="center" sx={{ mb: 1 }}>
              Time-series for{" "}
              <b>
                {isMultipleNodesMode
                  ? nodes?.map((x) => x.label).join(", ")
                  : nodes?.[0]?.label || ""}
              </b>{" "}
            </Typography>
            <TimeSeriesChart
              title={"Timeseries"}
              nodes={nodes}
              isMultipleNodesMode={isMultipleNodesMode}
              showAsTable={showAsTable}
              showMeanAggregates={options.isMean}
              showMeanLine={options.showMeanLine}
              showStDev={true} // TODO tr: configure by option
              percentiles={percentiles}
              aggregateData={aggregateData}
              historicNodes={historicNodes}
              showHistoric={showHistoric}
              lagShift={lagShift}
              ref={chartRef}
              runs={runs}
            />
          </Box>
        )}
      </Grid>
    </Grid>
  )
}
